import { useMemo, useState } from "react";
import {
  emptyFilter,
  validateFilter,
  type FieldDefinition,
  type Filter,
} from "@estelle/domain";

import { FilterBuilder } from "@/components/filters/FilterBuilder";
import { Button } from "@/components/ui/button";

/**
 * The full AND/OR tree editor, as a popover's contents rather than its own
 * button — the filter bar opens it two ways (the "N conditions (advanced)"
 * chip when the filter already has a group or an OR, and "Advanced" in the
 * field dropdown when someone wants one) and both need the same draft, the
 * same Apply and the same Clear rather than two popovers that happen to look
 * alike.
 *
 * A draft rather than editing `value` live, same reasoning as the toolbar's
 * old Filter button: a list that refetched on every keystroke inside a
 * half-built condition would be both slower and harder to reason about. The
 * draft starts fresh because Radix unmounts a closed popover's content, so a
 * plain `useState(value)` is reopened rather than carried over from the
 * previous time this was open.
 *
 * The conditions scroll and the footer does not. A long filter used to push
 * Apply below the bottom of the screen with nothing to scroll, so it could not
 * be reached at all (#492 made the rows taller, and that is how it was found).
 * The height comes from the popover itself: Radix publishes the room left
 * between the trigger and the edge of the window as
 * `--radix-popover-content-available-height`, which is right wherever the
 * trigger is and whichever side it opens on, and a window is not an assumption
 * made here. The three rem take off the popover's own padding and border and
 * leave a little air under it.
 */
export function AdvancedFilterEditor({
  value,
  fields,
  onApply,
  onClose,
}: {
  value: Filter;
  fields: FieldDefinition[];
  onApply: (filter: Filter) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(value);
  // Said only once Apply has been tried (#633), not while a value is chosen.
  const [attempted, setAttempted] = useState(false);
  const problems = useMemo(
    () => validateFilter(draft, fields),
    [draft, fields],
  );

  return (
    <div
      className="flex w-[41rem] max-w-[90vw] flex-col"
      style={{
        maxHeight:
          "calc(var(--radix-popover-content-available-height, 100dvh) - 3rem)",
      }}
    >
      {/* Padded and pulled back out so the focus rings on the controls are not
          clipped by the scroll area. */}
      <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1 pb-3">
        <FilterBuilder value={draft} fields={fields} onChange={setDraft} />
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2 border-t pt-3">
        <Button
          size="sm"
          onClick={() => {
            if (problems.length > 0) {
              setAttempted(true);
              return;
            }
            onApply(draft);
            onClose();
          }}
        >
          Apply
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            onApply(emptyFilter());
            onClose();
          }}
        >
          Clear
        </Button>
        {attempted && problems.length > 0 ? (
          <span className="text-destructive ml-auto text-xs">
            {problems.length} incomplete{" "}
            {problems.length === 1 ? "condition" : "conditions"}
          </span>
        ) : null}
      </div>
    </div>
  );
}
